import { Injectable, inject } from '@angular/core';
// seaf-n8n-resilience-retry-service
import { supabase } from '../supabase';
import { AppointmentService } from './appointment.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class WebhookRetryService {
  private apptService = inject(AppointmentService);
  private toast = inject(ToastService);

  private retryInterval: any;
  private cleanupInterval: any;
  private backoffInterval: any;

  constructor() {
    this.startCleanupLoop();
    this.startStuckCheckLoop();
    this.startBackoffRetryLoop();
  }

  /**
   * Ciclo de limpeza automática (2 dias) executado periodicamente (a cada 12h).
   */
  private startCleanupLoop() {
    this.cleanupInterval = setInterval(() => {
      this.runCleanup();
    }, 12 * 60 * 60 * 1000);
  }

  /**
   * Ciclo de checagem de logs travados (a cada 30 minutos).
   * NáO reseta RESENDING rapidamente - o fluxo n8n pode demorar para evitar
   * banimento no WhatsApp. Só resetamos logs que estão há mais de 30 minutos
   * presos (provavelmente Edge Function travou).
   */
  private startStuckCheckLoop() {
    this.retryInterval = setInterval(() => {
      this.resetStuckLogs();
    }, 5 * 60 * 1000);
  }

  /**
   * Ciclo de retry com backoff exponencial (a cada 10 minutos).
   * Re-dispara logs FAILED que ainda não atingiram o limite de tentativas.
   */
  private startBackoffRetryLoop() {
    // Executa uma vez após 30s do boot, depois a cada 10 min
    setTimeout(() => this.retryFailedLogs(), 30000);
    this.backoffInterval = setInterval(() => {
      this.retryFailedLogs();
    }, 10 * 60 * 1000);
  }

  /**
   * Retry automático de logs FAILED com backoff exponencial.
   * Backoff: 2^tentativas * 5 minutos (10min, 20min, 40min, 80min, 160min)
   * Máximo de 5 tentativas.
   */
  async retryFailedLogs() {
    const now = new Date().toISOString();

    // 0. Trava de Segurança: Só re-dispara se houver municípios ativos com n8n habilitado
    const { data: activeMuns } = await supabase
      .from('municipalities')
      .select('id')
      .eq('is_active', true)
      .eq('enable_n8n', true);

    const activeMunIds = new Set((activeMuns || []).map((m: any) => m.id));
    if (activeMunIds.size === 0) {
      // Disparos automáticos pausados com segurança (nenhum município com n8n ativo no momento)
      return;
    }

    // Buscar logs FAILED elegíveis para retry
    const { data: failedLogs, error } = await supabase
      .from('webhook_logs')
      .select('id, attempts, created_at, next_retry_at, municipality_id')
      .eq('status', 'FAILED')
      .or(`next_retry_at.is.null,next_retry_at.lte.${now}`)
      .lt('attempts', 5)
      .order('created_at', { ascending: true })
      .limit(10);

    if (error) {
      console.error('[WebhookRetryService] Erro ao buscar logs para retry:', error);
      return;
    }

    if (!failedLogs || failedLogs.length === 0) return;

    // Filtrar apenas logs de municípios que estão com o n8n ativo no momento
    const eligibleLogs = failedLogs.filter((log: any) => activeMunIds.has(log.municipality_id));
    if (eligibleLogs.length === 0) return;

    console.log(`[WebhookRetryService] Re-disparando ${eligibleLogs.length} logs com backoff para municípios ativos...`);

    for (const log of eligibleLogs) {
      const attempts = (log.attempts || 0) + 1;
      // Backoff exponencial: 2^n * 5 minutos
      const backoffMinutes = Math.pow(2, attempts) * 5;
      const nextRetry = new Date(Date.now() + backoffMinutes * 60 * 1000).toISOString();

      await supabase.from('webhook_logs').update({
        status: 'PENDING',
        attempts: attempts,
        next_retry_at: nextRetry,
        last_attempt: new Date().toISOString()
      }).eq('id', log.id);
    }
  }

  /**
   * Reseta logs travados no status RESENDING por mais de 5 minutos (Edge Function crash)
   * e logs travados no status PROCESSING por mais de 30 minutos (Falha no Callback do n8n).
   */
  async resetStuckLogs() {
    const fiveMinutesAgo = new Date();
    fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);

    const { data: stuckResending } = await supabase
      .from('webhook_logs')
      .update({ status: 'FAILED', response_body: 'Edge function timeout interno (RESENDING travado)' })
      .eq('status', 'RESENDING')
      .lt('last_attempt', fiveMinutesAgo.toISOString())
      .select('id');

    if (stuckResending && stuckResending.length > 0) {
      console.log(`[WebhookRetryService] Resetados ${stuckResending.length} logs que ficaram travados no envio resending.`);
    }

    const thirtyMinutesAgo = new Date();
    thirtyMinutesAgo.setMinutes(thirtyMinutesAgo.getMinutes() - 30);

    const { data: stuckProcessing } = await supabase
      .from('webhook_logs')
      .update({ status: 'FAILED', response_body: 'Timeout assíncrono do n8n (sem callback após 30 min)' })
      .eq('status', 'PROCESSING')
      .lt('last_attempt', thirtyMinutesAgo.toISOString())
      .select('id');

    if (stuckProcessing && stuckProcessing.length > 0) {
      console.log(`[WebhookRetryService] Resetados ${stuckProcessing.length} logs perdidos no n8n (Timeout Assíncrono).`);
    }
  }

  /**
   * Executa a limpeza de logs antigos (> 2 dias).
   */
  async runCleanup() {
    console.log('[WebhookRetryService] Executando limpeza de logs antigos (> 2 dias)...');
    try {
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

      const { error } = await supabase
        .from('webhook_logs')
        .delete()
        .lt('created_at', twoDaysAgo.toISOString());

      if (error) {
        console.error('[WebhookRetryService] Erro na limpeza de logs:', error);
      }
    } catch (err) {
      console.error('[WebhookRetryService] Falha na rotina de limpeza:', err);
    }
  }

  ngOnDestroy() {
    if (this.retryInterval) clearInterval(this.retryInterval);
    if (this.cleanupInterval) clearInterval(this.cleanupInterval);
    if (this.backoffInterval) clearInterval(this.backoffInterval);
  }
}

